import { distinctUntilChanged } from 'rxjs/operators';
import {
  AfterViewInit,
  Component,
  ContentChild,
  Directive,
  DoCheck,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
  ViewContainerRef
} from "@angular/core";
import { NgControl } from "@angular/forms";
import { TranslateService } from "@ngx-translate/core";
import { NgbPopover } from "@ng-bootstrap/ng-bootstrap";
import { ValidationMessageService } from './validation-message.service';
import { FormLayout } from './form-layout';
import { CapFormRowDirective } from './cap-form-row.directive';
import { Subscription } from "rxjs";
import { keysToTranslate } from '../../../i18n/keys-to-translate';

/**
 * This components decorates the enclosed form control as a bootstrap form control, adding labels, help texts,
 * and validation messages in the style of the enclosing FormLayout.
 *
 * Usage example:
 * <pre>
 * <cap-form-control labelKey="Subject" helpKey="A brief summary of your issue" invalidKey="A subject is required">
 *   <input name="subject" [(ngModel)]="issue.subject" type="text" required>
 * </cap-form-control>
 * </pre>
 *
 * The projected content may contain additional markup, such as a <div class="input-group-append">.
 *
 * Supports input, select, textarea, bsg-datepicker, and custom components. Each form-control must contain exactly one
 * such component. Other controls can coexist in the same form, but you'll have to decorate them manually.
 */
@Component({
  selector: "cap-form-control",
  host: {
    '[class]': 'groupClasses',
  },
  template: `
    <ng-container capFormControlViewContainer></ng-container>
    <ng-template capFormControlSubTemplate>
      <div [class]="labelWrapperClasses" [class.sr-only]="!labelKey" *ngIf="!checkboxLabel">
        <label capFormControlLabel class="bsg-label" [ngClass]="labelClasses"
               [class.is-invalid]="invalid" [class.is-read-only]="readonly">
          {{labelKey || placeholderKey | translate}}
        </label>
      </div>
      <div [class]="controlWrapperClasses" [ngbPopover]="popoverTemplate" placement="bottom"
           #popover="ngbPopover" container="body" triggers="manual"
           (focusin)="active = true" (focusout)="active = false"
           (mouseenter)="active = true" (mouseleave)="active = false">
        <div class="custom-control custom-checkbox mt-2" *ngIf="checkboxLabel">
          <ng-container *ngTemplateOutlet="input"></ng-container>
          <label capFormControlLabel class="custom-control-label">{{labelKey | translate}}</label>
        </div>
        <div class="input-group" *ngIf="!checkboxLabel">
          <ng-container *ngTemplateOutlet="input"></ng-container>
          <ng-content select=".input-group-append"></ng-content>
        </div>
      </div>
    </ng-template>
    <ng-template #popoverTemplate>
      <ul class="popover-list">
        <li capFormControlHelp *ngIf="helpKey">{{helpKey | translate}}</li>
        <ng-container *ngIf="invalid">
          <li *ngIf="helpKey">
            <hr class="my-1">
          </li>
          <li class="text-danger font-weight-bold" *ngFor="let m of invalidMessages">
            <span class="fas fa-exclamation-circle fa-fw mr-2"></span>{{m}}
          </li>
        </ng-container>
      </ul>
    </ng-template>
    <ng-template #input>
      <!--
        for some arcane reason, only the first occurrence of ng-content projects content,
        meaning ng-content still consumes the content inside an *ngIf="false".
        By wrapping it into a template, we ensure there is only a single ng-content,
        that we can splice it into the appropriate wrapper element.
        See also https://stackoverflow.com/questions/41593973/how-to-conditionally-wrap-a-div-around-ng-content
      -->
      <ng-content></ng-content>
    </ng-template>
  `,
})
export class CapFormControlComponent implements OnInit, AfterViewInit, DoCheck, OnDestroy {

  @ContentChild(NgControl, {read: ElementRef, static: true})
  inputRefFromContent: ElementRef;
  inputRefOverride: ElementRef;

  private get inputRef() {
    return this.inputRefOverride || this.inputRefFromContent;
  }

  @ContentChild(NgControl, {static: true})
  control: NgControl;

  @Input() labelKey: string;
  @Input() placeholderKey: string; // translation key for a placeholder. Will be accessible even if the screen reader does not support placeholders.
  @Input() helpKey: string;
  /** Translation key to override the default validation messages */
  @Input() invalidKey: string;

  @Input("column-classes")
  set columnClasses(s: string) {
    const tokens = s.split(/,/);
    this.labelWrapperClasses = tokens[0] + ' bsg-label-wrapper';
    this.controlWrapperClasses = (tokens[1] || tokens[0]) + ' bsg-input-wrapper';
  }

  @ViewChild('popover', {static: true})
  popover: NgbPopover;

  /* the view container we should render to */
  viewContainerRef: ViewContainerRef;

  label: HTMLLabelElement;

  help: HTMLElement;

  invalid$ = new EventEmitter<boolean>();
  readonly$ = new EventEmitter<boolean>();

  /** whether the user is currently interacting with this form control */
  active = false;
  supressPopover = false;

  groupClasses: string;
  labelWrapperClasses: string;
  controlWrapperClasses: string;
  labelClasses: string;
  inputClasses: string[];

  checkboxLabel = false;

  private subscription: Subscription = new Subscription();

  constructor(private layout: FormLayout, viewContainerRef: ViewContainerRef,
              private validationMessageService: ValidationMessageService, private translate: TranslateService) {
    keysToTranslate([
      'geburtsdatumInZukunft'
    ])
    if (layout instanceof CapFormRowDirective) {
      // render to siblings of <cap-form-component>
      // so cols are immediate children of rows
      // as needed by bootstrap (flexbox)
      this.viewContainerRef = viewContainerRef;
    } else {
      // render into <cap-form-component>
    }
  }

  ngOnInit() {
    // the defaults
    this.groupClasses = this.layout.groupClasses;
    this.labelWrapperClasses = this.labelWrapperClasses || this.layout.labelWrapperClasses;
    this.controlWrapperClasses = this.controlWrapperClasses || this.layout.controlWrapperClasses;
    this.labelClasses = this.layout.labelClasses;
    this.inputClasses = ["form-control", "bsg-form-control"];

    // ... and the overrides for special cases
    const input = this.inputRef.nativeElement;
    if (input.type === "checkbox") {
      this.checkboxLabel = true;
      this.inputClasses = ["custom-control-input"];
    } else {
      this.groupClasses += " bsg-form-group";
    }

    if (input instanceof HTMLTextAreaElement) {
      this.labelClasses += " h-100";
    }
  }

  ngAfterViewInit() {
    const label = this.label;
    const input = <HTMLInputElement>this.inputRef.nativeElement;
    const help = this.help;

    if (label) {
      if (!input.id) {
        input.id = this.control.name.toString();
      }
      label.setAttribute("for", input.id);
    }

    if (this.placeholderKey) {
      const key = this.placeholderKey;
      this.subscription.add(this.translate.stream(key).pipe().subscribe(translated => {
        input.setAttribute("placeholder", translated);
      }));
    }

    if (help) {
      help.id = input.id + "Help";
      input.setAttribute("aria-describedby", help.id);
    }

    for (const c of this.inputClasses) {
      input.classList.add(c); // we need the loop because IE 11 only accepts a single argument to classList.add
    }
    this.invalid$.pipe(distinctUntilChanged()).subscribe(classSetter(input, "is-invalid"));
    this.readonly$.pipe(distinctUntilChanged()).subscribe(classSetter(input, "is-read-only"));
  }

  ngDoCheck() {
    this.invalid$.emit(this.invalid); // we detect changes manually because control does not publish when it is touched :-(
    this.readonly$.emit(this.readonly); // nor when it becomes read-only ...

    const showPopover = (this.helpKey || this.invalid) && this.active && !this.supressPopover;
    if (this.popover && this.popover.isOpen() !== showPopover) {
      if (showPopover) {
        this.popover.open();
      } else {
        this.popover.close();
      }
    }
  }

  get invalid() {
    return !!(this.control && this.control.touched && this.control.errors);
  }

  get readonly() {
    return this.control.disabled;
  }

  get invalidMessages() {
    const invalidKey = this.invalidKey;
    if (invalidKey) {
      return [this.translate.instant(invalidKey)];
    } else {
      const labelKey = this.labelKey || this.placeholderKey;
      const label = this.translate.instant(labelKey);
      return this.validationMessageService.messagesFor(label, this.control.errors!);
    }
  }

  /** invoke this if the child is not found by the content query (for instance because it is in a nested component) */
  registerChild(inputRef: ElementRef, control?: NgControl) {
    this.inputRefOverride = inputRef;
    if (control) {
      this.control = control;
    }
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }
}

@Directive({
  selector: '[capFormControlViewContainer]'
})
export class CapFormControlViewContainerDirective {
  constructor(c: CapFormControlComponent, viewContainerRef: ViewContainerRef) {
    c.viewContainerRef = c.viewContainerRef || viewContainerRef;
  }
}

// we can't use ViewChild, because that gets resolved after view creation,
// which causes an ExpressionChangedAfterItHasBeenCheckedError when we instantiate the template in FormRowDirective
@Directive({
  selector: '[capFormControlSubTemplate]'
})
export class CapFormControlSubTemplateDirective {
  constructor(c: CapFormControlComponent, templateRef: TemplateRef<void>) {
    c.viewContainerRef.createEmbeddedView(templateRef);
  }
}

// @ViewChild doesn't see into child templates ... we therefore use these directives to identify the nodes of interest
@Directive({
  selector: '[capFormControlLabel]'
})
export class CapFormControlLabelDirective {
  constructor(c: CapFormControlComponent, elementRef: ElementRef) {
    c.label = elementRef.nativeElement;
  }
}

@Directive({
  selector: '[capFormControlHelp]'
})
export class CapFormControlHelpDirective {
  constructor(c: CapFormControlComponent, elementRef: ElementRef) {
    c.help = elementRef.nativeElement;
  }
}

/**
 * A subscriber function to toggle a CSS class on an element.
 */
function classSetter(input: HTMLElement, className: string) {
  return value => {
    if (value) {
      input.classList.add(className);
    } else {
      input.classList.remove(className);
    }
  };
}
